import { addressSchema, UnauthorizedError } from '@omecare/utils';
import { UseQueryResult, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import http from '@/utils/http.service';

export const getDoctorAttendingWithAccountingsItemResponseSchema = z.object({
  id: z.string().uuid(),
  firstname: z.string(),
  lastname: z.string(),
  office: z.object({
    id: z.string(),
    name: z.string(),
    address: addressSchema,
  }),
  createdAt: z.coerce.date(),
});

export type GetDoctorAttendingWithAccountingsItemResponse = z.infer<
  typeof getDoctorAttendingWithAccountingsItemResponseSchema
>;

export const getDoctorAttendingsWithAccountingsResponseSchema = z.object({
  data: z.array(getDoctorAttendingWithAccountingsItemResponseSchema),
});

export type GetDoctorAttendingsWithAccountingsResponse = z.infer<
  typeof getDoctorAttendingsWithAccountingsResponseSchema
>;

export type GetDoctorAttendingsWithAccountingsParams = {
  page: number;
  per_page: number;
  search?: string;
};

const getDoctorAttendings =
  (params: GetDoctorAttendingsWithAccountingsParams) => async () => {
    return http.doctorAttendings
      .getListWithAccountings(params)
      .then<GetDoctorAttendingsWithAccountingsResponse>(
        ({ response, transform }) => {
          return transform.handle({
            response,
            attributes: {
              firstname: true,
              lastname: true,
              createdAt: true,
            },
            relations: {
              office: true,
            },
            schema: getDoctorAttendingsWithAccountingsResponseSchema,
          }) as GetDoctorAttendingsWithAccountingsResponse;
        },
      );
  };

export const useGetDoctorAttendingsWithAccountingsQuery = (
  params: GetDoctorAttendingsWithAccountingsParams,
): UseQueryResult<
  GetDoctorAttendingsWithAccountingsResponse,
  UnauthorizedError
> => {
  return useQuery({
    queryFn: getDoctorAttendings(params),
    queryKey: [
      'doctor-attendings-with-accountings',
      params.page,
      params.per_page,
      params.search,
    ],
    retry: false,
  });
};
